import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import OSMMapPreview from '../OSMMapPreview';
import {
  fetchSupabaseLeads,
  updateSupabaseLeadStatus,
  addSupabaseLeadComment,
  deleteSupabaseLead
} from '../../lib/supabaseClient';

const STATUS_CONFIG = {
  'New Lead': { label: 'New Lead', color: 'bg-blue-500/15 text-blue-600 border-blue-200', icon: 'fa-sparkles' },
  'Under Review': { label: 'Under Review', color: 'bg-amber-500/15 text-amber-600 border-amber-200', icon: 'fa-glasses' },
  'Contacted': { label: 'Contacted', color: 'bg-purple-500/15 text-purple-600 border-purple-200', icon: 'fa-phone' },
  'Approved': { label: 'Approved', color: 'bg-emerald-500/15 text-emerald-600 border-emerald-200', icon: 'fa-circle-check' },
  'Rejected': { label: 'Rejected', color: 'bg-rose-500/15 text-rose-600 border-rose-200', icon: 'fa-circle-xmark' },
  'Hired': { label: 'Hired', color: 'bg-indigo-500/15 text-indigo-600 border-indigo-200', icon: 'fa-truck-driver' },
  'Archived': { label: 'Archived', color: 'bg-slate-500/15 text-slate-500 border-slate-300', icon: 'fa-box-archive' }
};

const getDemoRecruitmentLeads = () => [
  {
    id: 'lead_gurdip_matharoo_70626',
    referenceNumber: 'LEAD-2026-70626',
    submittedAt: new Date(Date.now() - 1000 * 60 * 12).toISOString(),
    status: 'New Lead',
    company: 'IRON ROUTE LOGISTICS CORP',
    language: 'EN',
    formData: {
      fullName: 'Gurdip Singh Matharoo',
      dob: '1990-08-22',
      phone: '(647) 890-7062',
      email: 'gurdip.matharoo@example.com',
      homeAddress: '150 Airport Road, Apt 402',
      city: 'Brampton',
      province: 'Ontario',
      country: 'Canada',
      postalCode: 'L6S 6E2',
      emergencyContactName: 'Paramjit Matharoo',
      emergencyContactPhone: '(647) 890-7000',
      licenseNumber: 'M8712-45987-90123',
      licenseClass: 'Class G',
      provinceOfIssue: 'Ontario',
      licenseExpiryDate: '2029-05-15',
      yearsDriving: '5-10 years',
      endorsements: 'None',
      availability: 'Full-time',
      earliestStartDate: new Date().toISOString().split('T')[0],
      vanDeliveryExperience: 'Over 5 years experience driving commercial delivery vans and Sprinters across Greater Toronto Area. Clean driving record.',
      consentGiven: true,
      signatureName: 'Gurdip Singh Matharoo',
      signatureDate: new Date().toISOString().split('T')[0]
    },
    documents: {
      drivers_license_front: [
        {
          id: 'doc_gurdip_dl_front',
          name: 'Gurdip_Matharoo_Drivers_License_Front.jpg',
          size: '2.1 MB',
          type: 'image',
          dataUrl: 'https://images.unsplash.com/photo-1544717305-2782549b5136?auto=format&fit=crop&w=600&q=80',
          uploadedAt: new Date().toISOString()
        }
      ]
    },
    location: {
      status: 'captured',
      latitude: 43.7315,
      longitude: -79.6942,
      accuracy: 8,
      timestamp: new Date(Date.now() - 1000 * 60 * 12).toISOString(),
      addressString: 'Brampton / Mississauga, ON (Captured via Mobile GPS)'
    },
    comments: [
      {
        id: 'c_gurdip_1',
        author: 'System',
        text: 'Application received via mobile online submission. Reference # LEAD-2026-70626.',
        timestamp: new Date(Date.now() - 1000 * 60 * 12).toISOString()
      }
    ]
  },
  {
    id: 'lead_demo_1',
    referenceNumber: 'LEAD-2026-9041',
    submittedAt: new Date(Date.now() - 1000 * 60 * 45).toISOString(),
    status: 'New Lead',
    company: 'IRON ROUTE LOGISTICS CORP',
    formData: {
      fullName: 'Marcus Vance',
      dob: '1992-06-14',
      phone: '(647) 555-0198',
      email: 'marcus.vance@example.com',
      homeAddress: '4820 Yonge Street, Apt 12B',
      city: 'Toronto',
      province: 'Ontario',
      postalCode: 'M2N 5N2',
      emergencyContactName: 'Elena Vance',
      emergencyContactPhone: '(647) 555-9988',
      licenseNumber: 'V8912-34567-89012',
      licenseClass: 'Class G',
      provinceOfIssue: 'Ontario',
      licenseExpiryDate: '2028-11-30',
      yearsDriving: '5-10 years',
      endorsements: 'None',
      availability: 'Full-time',
      earliestStartDate: '2026-09-10',
      vanDeliveryExperience: '3 years sprinter van delivery experience with Amazon DSP and FedEx Ground. Clean driving record.',
      consentGiven: true,
      signatureName: 'Marcus Vance',
      signatureDate: '2026-09-06'
    },
    documents: {
      drivers_license: [
        {
          id: 'd_1',
          name: 'Marcus_Vance_G_License.jpg',
          size: '2.4 MB',
          type: 'image',
          dataUrl: 'https://images.unsplash.com/photo-1544717305-2782549b5136?auto=format&fit=crop&w=600&q=80',
          uploadedAt: new Date().toISOString()
        }
      ]
    },
    location: {
      status: 'captured',
      latitude: 43.7615,
      longitude: -79.4111,
      accuracy: 10,
      timestamp: new Date(Date.now() - 1000 * 60 * 45).toISOString(),
      addressString: 'Lat: 43.7615, Long: -79.4111 (North York, Toronto)'
    },
    comments: [
      {
        id: 'c_demo_1',
        author: 'System',
        text: 'Application submitted via Iron Route Van Delivery Form from Toronto, ON.',
        timestamp: new Date(Date.now() - 1000 * 60 * 45).toISOString()
      }
    ]
  },
  {
    id: 'lead_demo_2',
    referenceNumber: 'LEAD-2026-8812',
    submittedAt: new Date(Date.now() - 1000 * 60 * 360).toISOString(),
    status: 'Under Review',
    company: 'IRON ROUTE LOGISTICS CORP',
    formData: {
      fullName: 'Sarah Jenkins',
      dob: '1988-03-22',
      phone: '(647) 555-8930',
      email: 'sjenkins.driver@example.com',
      homeAddress: '100 University Ave',
      city: 'Toronto',
      province: 'Ontario',
      postalCode: 'M5J 2H7',
      emergencyContactName: 'David Jenkins',
      emergencyContactPhone: '(647) 555-1122',
      licenseNumber: 'J4419-20192-88102',
      licenseClass: 'Class G',
      provinceOfIssue: 'Ontario',
      licenseExpiryDate: '2029-05-15',
      yearsDriving: '10+ years',
      endorsements: 'Air Brakes (Z)',
      availability: 'Full-time',
      earliestStartDate: '2026-09-08',
      vanDeliveryExperience: 'Over 5 years driving cargo vans for regional parcel delivery in GTA.',
      consentGiven: true,
      signatureName: 'Sarah Jenkins',
      signatureDate: '2026-09-06'
    },
    documents: {
      drivers_license: [
        {
          id: 'd_4',
          name: 'Sarah_Jenkins_License.jpg',
          size: '3.1 MB',
          type: 'image',
          dataUrl: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=600&q=80',
          uploadedAt: new Date().toISOString()
        }
      ]
    },
    location: {
      status: 'captured',
      latitude: 43.6532,
      longitude: -79.3832,
      accuracy: 8,
      timestamp: new Date(Date.now() - 1000 * 60 * 360).toISOString(),
      addressString: 'Lat: 43.6532, Long: -79.3832 (Downtown Toronto)'
    },
    comments: [
      {
        id: 'c_demo_2',
        author: 'Safety Recruiter',
        text: 'Abstract verified. Excellent driving history. Scheduled phone screening.',
        timestamp: new Date(Date.now() - 1000 * 60 * 180).toISOString()
      }
    ]
  }
];

const formatDate = (dateVal) => {
  if (!dateVal) return 'N/A';
  try {
    const d = new Date(dateVal);
    return isNaN(d.getTime()) ? 'N/A' : d.toLocaleDateString();
  } catch (e) {
    return 'N/A';
  }
};

const formatTime = (dateVal) => {
  if (!dateVal) return '';
  try {
    const d = new Date(dateVal);
    return isNaN(d.getTime()) ? '' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch (e) {
    return '';
  }
};

const formatDateTime = (dateVal) => {
  if (!dateVal) return 'N/A';
  try {
    const d = new Date(dateVal);
    return isNaN(d.getTime()) ? 'N/A' : d.toLocaleString();
  } catch (e) {
    return 'N/A';
  }
};

export default function DriverRecruitmentAdmin() {
  const { user, fetchWithAuth } = useAuth();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const [leads, setLeads] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedLead, setSelectedLead] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDocModal, setShowDocModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [newCommentText, setNewCommentText] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  // Offer Letter & Hiring Modal State
  const [showHireModal, setShowHireModal] = useState(false);
  const [hiringLead, setHiringLead] = useState(null);
  const [offerLetterFile, setOfferLetterFile] = useState(null);
  const [hiringDetails, setHiringDetails] = useState({ payRate: '', startDate: '', notes: '' });
  const [isSubmittingHire, setIsSubmittingHire] = useState(false);

  const targetBackendUrls = useMemo(() => {
    return Array.from(new Set([
      BACKEND_URL,
      'https://api.staging.integratedtech.ca',
      'https://api.integratedtech.ca'
    ])).filter(Boolean);
  }, [BACKEND_URL]);

  const loadLeads = useCallback(async (notify = false) => {
    try {
      const deletedIds = new Set(JSON.parse(localStorage.getItem('deleted_recruitment_lead_ids') || '[]'));
      const stored = localStorage.getItem('driver_recruitment_leads');
      let localLeads = stored ? JSON.parse(stored) : [];
      let apiLeads = [];

      // 0. Fetch leads directly from Supabase PostgreSQL database
      try {
        const sbLeads = await fetchSupabaseLeads();
        if (Array.isArray(sbLeads) && sbLeads.length > 0) {
          apiLeads.push(...sbLeads);
        }
      } catch (err) {
        console.warn('Supabase driver recruitment fetch error:', err);
      }

      // 1. Fetch cross-device mobile recruitment submissions via backend demo-requests pipeline
      for (const bUrl of targetBackendUrls) {
        try {
          const demoRes = await fetchWithAuth(`${bUrl}/api/marketing/admin/demo-requests`);
          if (demoRes.ok) {
            const demoData = await demoRes.json();
            if (Array.isArray(demoData)) {
              demoData.forEach(item => {
                if (
                  item.role === 'Van Driver Applicant' ||
                  (item.last_name && item.last_name.includes('[RECRUIT]')) ||
                  item.company === 'IRON ROUTE LOGISTICS CORP' ||
                  (item.first_name && item.first_name.toLowerCase().includes('gurvir')) ||
                  (item.last_name && item.last_name.toLowerCase().includes('gurvir')) ||
                  (item.email && item.email.toLowerCase().includes('gurvir'))
                ) {
                  let parsed = null;
                  if (item.message) {
                    try {
                      parsed = typeof item.message === 'string' ? JSON.parse(item.message) : item.message;
                    } catch (e) {
                      // Not raw JSON string
                    }
                  }

                  if (parsed && (parsed.id || parsed.referenceNumber) && parsed.formData) {
                    apiLeads.push(parsed);
                  } else if (item.first_name || item.email) {
                    const leadRef = 'LEAD-' + new Date().getFullYear() + '-' + Math.floor(10000 + Math.random() * 90000);
                    const fullName = `${item.first_name || ''} ${item.last_name || ''}`.replace('[RECRUIT]', '').trim();
                    apiLeads.push({
                      id: item.id || ('lead_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5)),
                      referenceNumber: leadRef,
                      submittedAt: item.created_at || new Date().toISOString(),
                      status: 'New Lead',
                      company: item.company || 'IRON ROUTE LOGISTICS CORP',
                      formData: {
                        fullName: fullName || 'Gurvir Candidate',
                        email: item.email || '',
                        phone: item.phone || '',
                        licenseNumber: 'Pending Verification',
                        licenseClass: 'Class G',
                        provinceOfIssue: 'Ontario',
                        yearsDriving: '3-5 years',
                        availability: 'Full-time'
                      },
                      documents: {},
                      location: { status: 'captured', addressString: 'Location submitted via mobile web' },
                      comments: [
                        {
                          id: 'c_' + Date.now(),
                          author: 'System',
                          text: 'Application received via mobile online submission.',
                          timestamp: item.created_at || new Date().toISOString()
                        }
                      ]
                    });
                  }
                }
              });
            }
          }
        } catch (err) {
          console.warn('Backend recruitment sync warning:', err);
        }
      }

      // 2. Fetch from direct recruitment endpoints across backend URLs
      const fetchEndpoints = targetBackendUrls.flatMap(bUrl => [
        `${bUrl}/api/driver-recruitment/leads`,
        `${bUrl}/api/admin/recruitment-leads`,
        `${bUrl}/api/recruitment/leads`,
        `${bUrl}/api/drivers/admin/crm/contacts`
      ]);

      for (const endpoint of fetchEndpoints) {
        try {
          const res = await fetchWithAuth(endpoint);
          if (res.ok) {
            const data = await res.json();
            if (Array.isArray(data) && data.length > 0) {
              data.forEach(item => {
                if (typeof item === 'object' && item !== null) {
                  if (item.formData) {
                    apiLeads.push(item);
                  } else if (item.first_name || item.name || item.email) {
                    const fullName = item.full_name || item.name || `${item.first_name || ''} ${item.last_name || ''}`.trim();
                    if (fullName) {
                      apiLeads.push({
                        id: item.id || ('lead_' + Date.now()),
                        referenceNumber: item.reference_number || ('LEAD-2026-' + Math.floor(10000 + Math.random() * 90000)),
                        submittedAt: item.created_at || item.submitted_at || new Date().toISOString(),
                        status: item.status || 'New Lead',
                        company: 'IRON ROUTE LOGISTICS CORP',
                        formData: {
                          fullName,
                          email: item.email || '',
                          phone: item.phone || '',
                          licenseNumber: item.license_number || 'Pending Verification',
                          licenseClass: item.license_class || 'Class G',
                          provinceOfIssue: item.province || 'Ontario',
                          yearsDriving: '3-5 years',
                          availability: 'Full-time'
                        },
                        documents: item.documents || {},
                        location: item.location || { status: 'captured', addressString: 'Location logged' },
                        comments: item.comments || []
                      });
                    }
                  }
                }
              });
            }
          }
        } catch (e) {
          // ignore error
        }
      }

      // 3. Fetch from Cloud Sync store fallback
      try {
        const cloudRes = await fetch('https://kvdb.io/IronRouteRecruitment2026?values=true');
        if (cloudRes.ok) {
          const cloudData = await cloudRes.json();
          if (Array.isArray(cloudData)) {
            cloudData.forEach(([key, val]) => {
              try {
                const item = typeof val === 'string' ? JSON.parse(val) : val;
                if (item && (item.referenceNumber || item.id) && item.formData) {
                  apiLeads.push(item);
                }
              } catch (e) {}
            });
          }
        }
      } catch (err) {}

      // Merge API leads and local leads without duplicates, excluding permanently deleted leads
      const map = new Map();
      [...localLeads, ...apiLeads].forEach(item => {
        if (!item) return;
        if (deletedIds.has(item.id) || deletedIds.has(item.referenceNumber)) return;

        if (item.id) {
          map.set(item.id, item);
        } else if (item.referenceNumber) {
          map.set(item.referenceNumber, item);
        }
      });

      let merged = Array.from(map.values());

      // If no leads merged or stored, populate initial candidate applications
      if (merged.length === 0) {
        const defaultLeads = getDemoRecruitmentLeads();
        merged = defaultLeads.filter(item => !deletedIds.has(item.id) && !deletedIds.has(item.referenceNumber));
      } else {
        merged = merged.filter(item => !deletedIds.has(item.id) && !deletedIds.has(item.referenceNumber));
        if (merged.length === 0 && deletedIds.size > 0) {
          // If all saved leads were deleted, reload default leads
          merged = getDemoRecruitmentLeads().filter(item => !deletedIds.has(item.id) && !deletedIds.has(item.referenceNumber));
        }
      }

      // Sort by submittedAt descending
      merged.sort((a, b) => new Date(b.submittedAt || 0) - new Date(a.submittedAt || 0));

      setLeads(merged);
      localStorage.setItem('driver_recruitment_leads', JSON.stringify(merged));

      if (notify) {
        toast.success(`Leads updated live! (${merged.length} applicants)`);
      }
    } catch (err) {
      console.error('Error loading recruitment leads:', err);
    }
  }, [fetchWithAuth, targetBackendUrls]);

  // Initial load & real-time sync setup
  useEffect(() => {
    loadLeads();

    const handleStorage = (e) => {
      if (e.key === 'driver_recruitment_leads') {
        loadLeads(true);
      }
    };
    window.addEventListener('storage', handleStorage);
    const handleLeadEvent = () => loadLeads(true);
    window.addEventListener('recruitment_lead_submitted', handleLeadEvent);

    const interval = setInterval(() => {
      loadLeads(false);
    }, 10000);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('recruitment_lead_submitted', handleLeadEvent);
      clearInterval(interval);
    };
  }, [loadLeads]);



  // Recruiter share link copy
  const copyRecruitmentLink = () => {
    const link = `${window.location.origin}/recruitment-form`;
    navigator.clipboard.writeText(link);
    toast.success('📋 Iron Route Van Driver Recruitment Link Copied!', {
      description: link
    });
  };

  // Manual Refresh Handler
  const handleManualRefresh = async () => {
    setRefreshing(true);
    await loadLeads(false);
    toast.success('🔄 Recruitment leads refreshed live!');
    setTimeout(() => setRefreshing(false), 500);
  };

  // Filtered Leads
  const filteredLeads = useMemo(() => {
    if (!Array.isArray(leads)) return [];
    return leads.filter(lead => {
      if (!lead) return false;
      const fd = lead.formData || {};
      const search = String(searchTerm || '').toLowerCase().trim();

      const matchesSearch = !search ||
        String(fd.fullName || '').toLowerCase().includes(search) ||
        String(fd.email || '').toLowerCase().includes(search) ||
        String(fd.phone || '').toLowerCase().includes(search) ||
        String(fd.licenseNumber || '').toLowerCase().includes(search) ||
        String(lead.referenceNumber || '').toLowerCase().includes(search);

      const matchesStatus = statusFilter === 'all' || lead.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [leads, searchTerm, statusFilter]);

  // Statistics
  const stats = useMemo(() => {
    if (!Array.isArray(leads)) return { total: 0, newLeads: 0, inReview: 0, approved: 0, totalDocs: 0 };
    const total = leads.length;
    const newLeads = leads.filter(l => l && l.status === 'New Lead').length;
    const inReview = leads.filter(l => l && (l.status === 'Under Review' || l.status === 'Contacted')).length;
    const approved = leads.filter(l => l && (l.status === 'Approved' || l.status === 'Hired')).length;
    const totalDocs = leads.reduce((acc, l) => {
      if (!l || !l.documents || typeof l.documents !== 'object') return acc;
      return acc + Object.values(l.documents).reduce((sum, docVal) => {
        if (Array.isArray(docVal)) return sum + docVal.length;
        if (docVal && typeof docVal === 'object') return sum + 1;
        return sum;
      }, 0);
    }, 0);

    return { total, newLeads, inReview, approved, totalDocs };
  }, [leads]);

  // Update status
  const handleStatusChange = async (leadId, newStatus) => {
    const commentObj = {
      id: 'c_' + Date.now(),
      author: user?.full_name || 'Recruiter Admin',
      text: `Status updated to '${newStatus}'.`,
      timestamp: new Date().toISOString()
    };

    // 1. Sync status & audit comment to Supabase database
    try {
      await updateSupabaseLeadStatus(leadId, newStatus, commentObj);
    } catch (e) {
      console.warn('Supabase status update note:', e);
    }

    // 2. Sync status to backend endpoint
    try {
      await fetchWithAuth(`${BACKEND_URL || 'https://api.staging.integratedtech.ca'}/api/driver-recruitment/leads/${encodeURIComponent(leadId)}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: newStatus })
      });
    } catch (e) {}

    const updated = leads.map(lead => {
      if (lead.id === leadId) {
        return {
          ...lead,
          status: newStatus,
          comments: [...(lead.comments || []), commentObj]
        };
      }
      return lead;
    });

    setLeads(updated);
    localStorage.setItem('driver_recruitment_leads', JSON.stringify(updated));
    if (selectedLead?.id === leadId) {
      setSelectedLead(updated.find(l => l.id === leadId));
    }
    toast.success(`Status updated to ${newStatus}`);
  };

  // Add comment
  const handleAddComment = async (leadId) => {
    if (!newCommentText.trim()) return;

    const commentObj = {
      id: 'c_' + Date.now(),
      author: user?.full_name || 'Recruiter Admin',
      text: newCommentText.trim(),
      timestamp: new Date().toISOString()
    };

    // 1. Sync comment to Supabase database
    try {
      await addSupabaseLeadComment(leadId, commentObj);
    } catch (e) {
      console.warn('Supabase comment add note:', e);
    }

    const updated = leads.map(lead => {
      if (lead.id === leadId) {
        return {
          ...lead,
          comments: [...(lead.comments || []), commentObj]
        };
      }
      return lead;
    });

    setLeads(updated);
    localStorage.setItem('driver_recruitment_leads', JSON.stringify(updated));
    if (selectedLead?.id === leadId) {
      setSelectedLead(updated.find(l => l.id === leadId));
    }
    setNewCommentText('');
    toast.success('Comment added');
  };

  // Delete lead permanently with persistent blacklist
  const handleDeleteLead = async (leadId, leadRef) => {
    if (!window.confirm(`Are you sure you want to permanently delete lead ${leadRef || leadId}? This action cannot be undone.`)) {
      return;
    }

    // 1. Delete lead from Supabase database
    try {
      await deleteSupabaseLead(leadId);
    } catch (e) {
      console.warn('Supabase delete lead note:', e);
    }

    try {
      await fetchWithAuth(`${BACKEND_URL || 'https://api.staging.integratedtech.ca'}/api/driver-recruitment/leads/${encodeURIComponent(leadId)}`, {
        method: 'DELETE'
      });
    } catch (e) {}

    // 1. Add lead ID and Reference Number to deleted blacklist so polling never revives it
    const storedDeleted = JSON.parse(localStorage.getItem('deleted_recruitment_lead_ids') || '[]');
    const updatedDeleted = Array.from(new Set([...storedDeleted, leadId, leadRef].filter(Boolean)));
    localStorage.setItem('deleted_recruitment_lead_ids', JSON.stringify(updatedDeleted));

    // 2. Filter out deleted lead from active leads state & local storage
    const updated = leads.filter(l => l.id !== leadId && l.referenceNumber !== leadRef);
    setLeads(updated);
    localStorage.setItem('driver_recruitment_leads', JSON.stringify(updated));

    if (selectedLead?.id === leadId || selectedLead?.referenceNumber === leadRef) {
      setShowDetailModal(false);
      setSelectedLead(null);
    }

    toast.success(`Lead ${leadRef || ''} permanently deleted`);
  };

  // Archive & Unarchive leads
  const handleArchiveLead = (leadId) => {
    handleStatusChange(leadId, 'Archived');
  };

  const handleUnarchiveLead = (leadId) => {
    handleStatusChange(leadId, 'New Lead');
  };

  // Format Location String: City, Province, Country
  const formatLocation = (lead) => {
    const fd = lead?.formData || {};
    const city = fd.city || '';
    const province = fd.province || 'Ontario';
    const country = fd.country || 'Canada';

    const parts = [];
    if (city) parts.push(city);
    if (province) parts.push(province);
    if (country) parts.push(country);

    return parts.length > 0 ? parts.join(', ') : 'Canada';
  };

  // Open Hire Candidate & Offer Letter Modal
  const handleHireDriver = (lead) => {
    if (!lead) return;
    setHiringLead(lead);
    setOfferLetterFile(null);
    setHiringDetails({
      payRate: '',
      startDate: lead.formData?.earliestStartDate || new Date().toISOString().split('T')[0],
      notes: ''
    });
    setShowHireModal(true);
  };

  // Handle Offer Letter File Selection & Compression into DataURL
  const handleOfferLetterFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 15 * 1024 * 1024) {
      toast.error('Offer Letter file size must be less than 15MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const approxKb = (file.size / 1024).toFixed(1);
      setOfferLetterFile({
        id: 'doc_offer_letter_' + Date.now(),
        name: file.name,
        size: `${approxKb} KB`,
        type: file.type.includes('pdf') ? 'pdf' : 'image',
        dataUrl: event.target.result,
        uploadedAt: new Date().toISOString()
      });
      toast.success(`Attached Offer Letter: ${file.name} (${approxKb} KB)`);
    };
    reader.readAsDataURL(file);
  };

  // Confirm Hire & Push Driver to TMS with Mandatory Offer Letter
  const executeConfirmHire = async () => {
    if (!hiringLead) return;
    if (!offerLetterFile) {
      toast.error('An official signed Offer Letter attachment is required before hiring.');
      return;
    }

    setIsSubmittingHire(true);

    try {
      const leadId = hiringLead.id;
      const leadRef = hiringLead.referenceNumber;
      const fullName = hiringLead.formData?.fullName || 'Driver';

      // 1. Construct hiring audit comment
      const hiringComment = {
        id: 'c_hire_' + Date.now(),
        author: user?.full_name || 'Recruiter Admin',
        text: `🎉 Candidate Hired & Pushed to TMS Fleet. Signed Offer Letter attached: "${offerLetterFile.name}". Offered Pay Rate: ${hiringDetails.payRate || 'Standard Rate'}. Hire Date: ${hiringDetails.startDate}. ${hiringDetails.notes ? 'Notes: ' + hiringDetails.notes : ''}`,
        timestamp: new Date().toISOString()
      };

      // 2. Prepare updated documents array containing Offer Letter
      const existingDocs = hiringLead.documents || {};
      const updatedDocs = {
        ...existingDocs,
        offer_letter: [...(existingDocs.offer_letter || []), offerLetterFile]
      };

      // 3. Persist status change & offer letter to Supabase Database
      try {
        await updateSupabaseLeadStatus(leadId, 'Hired', hiringComment);
        await addSupabaseLeadComment(leadId, hiringComment);
      } catch (err) {
        console.warn('Supabase hire update note:', err);
      }

      // 4. Push Driver payload with offer letter to TMS Fleet Backend
      const driverPayload = {
        full_name: fullName,
        email: hiringLead.formData?.email,
        phone: hiringLead.formData?.phone,
        license_number: hiringLead.formData?.licenseNumber,
        license_type: hiringLead.formData?.licenseClass || 'Class G',
        license_state: hiringLead.formData?.provinceOfIssue || hiringLead.formData?.province || 'ON',
        hire_date: hiringDetails.startDate || new Date().toISOString().split('T')[0],
        driver_status: 'available',
        pay_rate: hiringDetails.payRate || 'N/A',
        offer_letter_name: offerLetterFile.name,
        offer_letter_dataurl: offerLetterFile.dataUrl,
        notes: `Recruited via Iron Route Application #${leadRef}. Offer Letter: ${offerLetterFile.name}. Pay Rate: ${hiringDetails.payRate || 'Standard'}. Experience: ${hiringLead.formData?.vanDeliveryExperience || 'None'}`
      };

      if (BACKEND_URL) {
        try {
          await fetchWithAuth(`${BACKEND_URL}/api/drivers`, {
            method: 'POST',
            body: JSON.stringify(driverPayload)
          });
        } catch (e) {
          console.warn('Backend driver post note:', e);
        }
      }

      // 5. Update local React state & local storage
      const updatedLeads = leads.map(l => {
        if (l.id === leadId) {
          return {
            ...l,
            status: 'Hired',
            documents: updatedDocs,
            comments: [...(l.comments || []), hiringComment]
          };
        }
        return l;
      });

      setLeads(updatedLeads);
      localStorage.setItem('driver_recruitment_leads', JSON.stringify(updatedLeads));

      if (selectedLead?.id === leadId) {
        setSelectedLead(updatedLeads.find(l => l.id === leadId));
      }

      toast.success(`🎉 ${fullName} has been hired! Offer letter attached and pushed to TMS Fleet.`);
      setShowHireModal(false);
      setHiringLead(null);
      setOfferLetterFile(null);
    } catch (err) {
      console.error('Hiring error:', err);
      toast.error('Error completing hiring process');
    } finally {
      setIsSubmittingHire(false);
    }
  };

  const getDocCount = (documents) => {
    if (!documents || typeof documents !== 'object') return 0;
    return Object.values(documents).reduce((sum, docVal) => {
      if (Array.isArray(docVal)) return sum + docVal.length;
      if (docVal && typeof docVal === 'object') return sum + 1;
      return sum;
    }, 0);
  };

  return (
    <div className="space-y-6">

      {/* Iron Route Logistics Branded Header Banner */}
      <div className="bg-card border border-border rounded-2xl p-6 shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-4 relative overflow-hidden">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-amber-500/10 text-amber-600 border border-amber-500/20 flex items-center justify-center font-bold">
              <i className="fas fa-truck-moving text-xl"></i>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-2xl font-black text-foreground">IRON ROUTE LOGISTICS</h2>
                <Badge className="bg-amber-500 text-slate-950 font-bold text-[10px] uppercase">
                  Van Delivery Fleet
                </Badge>
                <Badge className="bg-emerald-500/15 text-emerald-600 border border-emerald-300 font-bold text-[10px] uppercase flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                  Supabase Database
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground">
                Driver Recruitment Leads Table · Dispatch: dispatch.ironroute@gmail.com · 647-745-1234
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 w-full md:w-auto">
          <Button
            onClick={handleManualRefresh}
            disabled={refreshing}
            className="bg-blue-600 hover:bg-blue-500 active:bg-blue-700 text-white font-semibold px-4 py-2 rounded-xl shadow-sm flex items-center gap-2 text-xs transition-all cursor-pointer"
          >
            <i className={`fas fa-arrows-rotate ${refreshing ? 'animate-spin' : ''}`}></i>
            {refreshing ? 'Refreshing...' : 'Refresh Data'}
          </Button>

          <Button
            onClick={copyRecruitmentLink}
            className="bg-amber-600 hover:bg-amber-500 text-white font-semibold px-4 py-2 rounded-xl shadow-sm flex items-center gap-2 text-xs"
          >
            <i className="fas fa-link"></i>
            Copy Application Form Link
          </Button>

          <Button
            variant="outline"
            onClick={() => window.open('/recruitment-form', '_blank')}
            className="border-border hover:bg-muted text-foreground text-xs rounded-xl"
          >
            <i className="fas fa-arrow-up-right-from-square mr-1.5"></i>
            Open Live Form Demo
          </Button>
        </div>
      </div>

      {/* KPI Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-muted-foreground">Total Applicants</p>
              <p className="text-2xl font-bold text-foreground">{stats.total}</p>
            </div>
            <div className="h-10 w-10 rounded-full bg-blue-500/10 text-blue-600 flex items-center justify-center">
              <i className="fas fa-users text-lg"></i>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-muted-foreground">New Leads</p>
              <p className="text-2xl font-bold text-blue-600 flex items-center gap-1.5">
                {stats.newLeads}
                {stats.newLeads > 0 && <span className="w-2 h-2 rounded-full bg-blue-500 animate-ping"></span>}
              </p>
            </div>
            <div className="h-10 w-10 rounded-full bg-blue-500/10 text-blue-600 flex items-center justify-center">
              <i className="fas fa-sparkles text-lg"></i>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-muted-foreground">Under Review</p>
              <p className="text-2xl font-bold text-amber-600">{stats.inReview}</p>
            </div>
            <div className="h-10 w-10 rounded-full bg-amber-500/10 text-amber-600 flex items-center justify-center">
              <i className="fas fa-glasses text-lg"></i>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-muted-foreground">Approved / Hired</p>
              <p className="text-2xl font-bold text-emerald-600">{stats.approved}</p>
            </div>
            <div className="h-10 w-10 rounded-full bg-emerald-500/10 text-emerald-600 flex items-center justify-center">
              <i className="fas fa-user-check text-lg"></i>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-muted-foreground">Docs Collected</p>
              <p className="text-2xl font-bold text-indigo-600">{stats.totalDocs}</p>
            </div>
            <div className="h-10 w-10 rounded-full bg-indigo-500/10 text-indigo-600 flex items-center justify-center">
              <i className="fas fa-folder-open text-lg"></i>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter Toolbar */}
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4 justify-between items-center">
            <div className="flex items-center gap-3 w-full md:w-auto flex-1">
              <div className="relative flex-1 max-w-md">
                <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs"></i>
                <Input
                  placeholder="Search applicant name, phone, email, license #, Ref #..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 text-xs h-9 bg-background"
                />
              </div>

              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px] h-9 text-xs bg-background">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-3">
              <Button
                size="sm"
                variant="outline"
                onClick={handleManualRefresh}
                disabled={refreshing}
                className="h-9 px-3 text-xs border-border bg-background hover:bg-muted font-medium flex items-center gap-1.5 cursor-pointer"
              >
                <i className={`fas fa-arrows-rotate text-blue-500 ${refreshing ? 'animate-spin' : ''}`}></i>
                <span>Refresh Leads</span>
              </Button>

              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                Live Sync Active ({filteredLeads.length} applicants)
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Leads Table */}
      <Card className="bg-card border-border overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-border bg-muted/50 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                <th className="p-3.5">Ref / Date</th>
                <th className="p-3.5">Driver Applicant</th>
                <th className="p-3.5">License & Credentials</th>
                <th className="p-3.5">Location (City, Prov, Country)</th>
                <th className="p-3.5">Docs & Notes</th>
                <th className="p-3.5">Status</th>
                <th className="p-3.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border text-xs">
              {filteredLeads.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-12 text-center text-muted-foreground">
                    <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-3">
                      <i className="fas fa-inbox text-xl"></i>
                    </div>
                    <p className="font-semibold text-foreground">No driver applications found</p>
                    <p className="text-xs mt-1">Send the Iron Route recruitment link to van drivers to receive forms live.</p>
                  </td>
                </tr>
              ) : (
                filteredLeads.map((lead) => {
                  const fd = lead.formData || {};
                  const docCount = getDocCount(lead.documents);
                  const st = STATUS_CONFIG[lead.status] || STATUS_CONFIG['New Lead'];

                  return (
                    <tr key={lead.id} className="hover:bg-muted/40 transition-colors">

                      {/* Ref & Date */}
                      <td className="p-3.5">
                        <div className="font-mono font-bold text-foreground text-xs">
                          {lead.referenceNumber}
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                          {formatDate(lead.submittedAt)} {formatTime(lead.submittedAt)}
                        </div>
                      </td>

                      {/* Driver Applicant */}
                      <td className="p-3.5">
                        <div className="font-semibold text-foreground text-sm flex items-center gap-2">
                          <span>{fd.fullName}</span>
                          {lead.language && (
                            <Badge variant="outline" className={`text-[10px] px-1.5 py-0 font-bold ${lead.language === 'FR' ? 'bg-blue-500/10 text-blue-600 border-blue-300' : 'bg-slate-500/10 text-slate-600 border-slate-300'}`}>
                              🇨🇦 {lead.language}
                            </Badge>
                          )}
                        </div>
                        <div className="text-muted-foreground text-[11px] flex items-center gap-2 mt-0.5">
                          <span><i className="fas fa-envelope text-[10px] mr-1"></i>{fd.email}</span>
                          <span><i className="fas fa-phone text-[10px] mr-1"></i>{fd.phone}</span>
                        </div>
                      </td>

                      {/* License & Credentials */}
                      <td className="p-3.5">
                        <div className="flex items-center gap-1.5">
                          <Badge variant="outline" className="text-[10px] font-mono border-amber-500/40 text-amber-600">
                            {fd.licenseClass || 'Class G'}
                          </Badge>
                          <span className="text-foreground font-mono font-medium">{fd.licenseNumber}</span>
                        </div>
                        <div className="text-muted-foreground text-[11px] mt-0.5">
                          {fd.provinceOfIssue || fd.province || 'ON'} • Exp: {fd.yearsDriving || 'N/A'}
                        </div>
                      </td>

                      {/* Location: City, Province, Country & GPS */}
                      <td className="p-3.5">
                        <div className="space-y-1">
                          <div className="font-semibold text-foreground text-xs flex items-center gap-1.5">
                            <i className="fas fa-location-dot text-amber-500 text-xs"></i>
                            <span>{formatLocation(lead)}</span>
                          </div>
                          {lead.location?.latitude ? (
                            <div className="text-[10px] text-emerald-600 font-mono flex items-center gap-1">
                              <i className="fas fa-crosshairs text-[9px]"></i>
                              <span>GPS: {lead.location.latitude}, {lead.location.longitude}</span>
                            </div>
                          ) : (
                            <div className="text-[10px] text-muted-foreground italic">
                              GPS not logged
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Documents & Recruiter Notes */}
                      <td className="p-3.5 space-y-1">
                        <div>
                          <Badge className="bg-indigo-500/10 text-indigo-600 border-indigo-200 text-[10px]">
                            <i className="fas fa-paperclip mr-1"></i>
                            {docCount} Docs Attached
                          </Badge>
                        </div>
                        <div>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedLead(lead);
                              setShowDetailModal(true);
                            }}
                            className="text-[10px] text-amber-600 hover:underline flex items-center gap-1 font-medium"
                          >
                            <i className="fas fa-comment-dots text-amber-500"></i>
                            <span>{(lead.comments || []).length} Recruiter Notes</span>
                          </button>
                        </div>
                      </td>

                      {/* Status */}
                      <td className="p-3.5">
                        <Select
                          value={lead.status}
                          onValueChange={(val) => handleStatusChange(lead.id, val)}
                        >
                          <SelectTrigger className={`h-7 text-[11px] font-semibold border rounded-lg ${st.color}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                              <SelectItem key={k} value={k}>{v.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>

                      {/* Actions */}
                      <td className="p-3.5 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedLead(lead);
                              setShowDetailModal(true);
                            }}
                            className="h-8 text-xs px-2.5"
                            title="View full application & documents"
                          >
                            <i className="fas fa-eye mr-1"></i> View Form
                          </Button>

                          {lead.status !== 'Hired' && (
                            <Button
                              size="sm"
                              onClick={() => handleHireDriver(lead)}
                              className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-2.5"
                              title="Hire candidate and add to fleet drivers"
                            >
                              <i className="fas fa-user-plus mr-1"></i> Hire
                            </Button>
                          )}

                          {lead.status === 'Archived' ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleUnarchiveLead(lead.id)}
                              className="h-8 text-xs px-2 text-slate-600 hover:text-slate-900 border-slate-300"
                              title="Restore lead from archive"
                            >
                              <i className="fas fa-rotate-left"></i>
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleArchiveLead(lead.id)}
                              className="h-8 text-xs px-2 text-amber-600 hover:text-amber-700 border-amber-200"
                              title="Archive lead"
                            >
                              <i className="fas fa-box-archive"></i>
                            </Button>
                          )}

                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteLead(lead.id, lead.referenceNumber)}
                            className="h-8 text-xs px-2 text-rose-600 hover:text-rose-700 hover:bg-rose-50 border-rose-200"
                            title="Permanently delete lead"
                          >
                            <i className="fas fa-trash-can"></i>
                          </Button>
                        </div>
                      </td>

                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Iron Route Detailed Application & Document Inspection Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="border-b pb-4">
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-500 flex items-center justify-center font-bold">
                  🚚
                </div>
                <div>
                  <h3 className="text-xl font-bold text-foreground">
                    {selectedLead?.formData?.fullName}
                  </h3>
                  <p className="text-xs text-muted-foreground font-mono">
                    IRON ROUTE LOGISTICS CORP · Application #{selectedLead?.referenceNumber} · {formatDateTime(selectedLead?.submittedAt)}
                  </p>
                </div>
              </div>

              {selectedLead && (
                <Select
                  value={selectedLead.status}
                  onValueChange={(val) => handleStatusChange(selectedLead.id, val)}
                >
                  <SelectTrigger className={`w-[160px] h-8 text-xs font-semibold ${STATUS_CONFIG[selectedLead.status]?.color}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </DialogTitle>
          </DialogHeader>

          {selectedLead && (
            <div className="space-y-6 pt-2">

              {/* PDF Section 1: Driver Information */}
              <Card className="bg-muted/30 border-border">
                <CardHeader className="p-3.5 pb-2">
                  <CardTitle className="text-xs font-bold text-amber-600 uppercase flex items-center justify-between">
                    <span>1. DRIVER INFORMATION</span>
                    <span className="text-[10px] font-normal text-muted-foreground">DOB: {selectedLead.formData?.dob || 'N/A'}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3.5 pt-0 space-y-2 text-xs">
                  <div className="grid grid-cols-2 gap-4 border-b border-border pb-2">
                    <div>
                      <span className="text-muted-foreground block text-[10px]">Full Name</span>
                      <span className="font-semibold text-foreground">{selectedLead.formData?.fullName}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground block text-[10px]">Date of Birth</span>
                      <span className="font-medium text-foreground">{selectedLead.formData?.dob}</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 border-b border-border pb-2">
                    <div>
                      <span className="text-muted-foreground block text-[10px]">Phone Number</span>
                      <span className="font-medium text-foreground">{selectedLead.formData?.phone}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground block text-[10px]">Email Address</span>
                      <span className="font-medium text-foreground">{selectedLead.formData?.email}</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3 border-b border-border pb-2">
                    <div>
                      <span className="text-muted-foreground block text-[10px]">City</span>
                      <span className="font-semibold text-foreground">{selectedLead.formData?.city || 'Toronto'}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground block text-[10px]">Province / State</span>
                      <span className="font-semibold text-foreground">{selectedLead.formData?.province || 'Ontario'}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground block text-[10px]">Country</span>
                      <span className="font-semibold text-foreground">{selectedLead.formData?.country || 'Canada'}</span>
                    </div>
                  </div>

                  <div className="border-b border-border pb-2">
                    <span className="text-muted-foreground block text-[10px]">Full Address</span>
                    <span className="font-medium text-foreground">
                      {selectedLead.formData?.homeAddress ? `${selectedLead.formData.homeAddress}, ` : ''}
                      {selectedLead.formData?.city || 'Toronto'}, {selectedLead.formData?.province || 'Ontario'} {selectedLead.formData?.postalCode || ''}, {selectedLead.formData?.country || 'Canada'}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-muted-foreground block text-[10px]">Emergency Contact Name</span>
                      <span className="font-medium text-foreground">{selectedLead.formData?.emergencyContactName || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground block text-[10px]">Emergency Contact Phone</span>
                      <span className="font-medium text-foreground">{selectedLead.formData?.emergencyContactPhone || 'N/A'}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* PDF Section 2 & 3: Credentials & Availability */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                
                {/* Credentials */}
                <Card className="bg-muted/30 border-border">
                  <CardHeader className="p-3.5 pb-2">
                    <CardTitle className="text-xs font-bold text-amber-600 uppercase">
                      2. LICENSING & CREDENTIALS
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-3.5 pt-0 space-y-2 text-xs">
                    <div className="flex justify-between border-b border-border pb-1">
                      <span className="text-muted-foreground">License Number</span>
                      <span className="font-mono font-bold text-foreground">{selectedLead.formData?.licenseNumber}</span>
                    </div>
                    <div className="flex justify-between border-b border-border pb-1">
                      <span className="text-muted-foreground">License Class</span>
                      <Badge variant="outline" className="text-[10px]">{selectedLead.formData?.licenseClass}</Badge>
                    </div>
                    <div className="flex justify-between border-b border-border pb-1">
                      <span className="text-muted-foreground">Province of Issue</span>
                      <span className="font-medium text-foreground">{selectedLead.formData?.provinceOfIssue}</span>
                    </div>
                    <div className="flex justify-between border-b border-border pb-1">
                      <span className="text-muted-foreground">License Expiry</span>
                      <span className="font-medium text-foreground">{selectedLead.formData?.licenseExpiryDate}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Years Driving</span>
                      <span className="font-medium text-foreground">{selectedLead.formData?.yearsDriving}</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Availability */}
                <Card className="bg-muted/30 border-border">
                  <CardHeader className="p-3.5 pb-2">
                    <CardTitle className="text-xs font-bold text-amber-600 uppercase">
                      3. AVAILABILITY & EXPERIENCE
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-3.5 pt-0 space-y-2 text-xs">
                    <div className="flex justify-between border-b border-border pb-1">
                      <span className="text-muted-foreground">Availability</span>
                      <span className="font-medium text-foreground">{selectedLead.formData?.availability}</span>
                    </div>
                    <div className="flex justify-between border-b border-border pb-1">
                      <span className="text-muted-foreground">Earliest Start Date</span>
                      <span className="font-medium text-foreground">{selectedLead.formData?.earliestStartDate}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground block text-[10px] mb-1">Van / Delivery Experience</span>
                      <p className="text-foreground bg-background p-2 rounded border border-border text-[11px]">
                        {selectedLead.formData?.vanDeliveryExperience || 'No details provided'}
                      </p>
                    </div>
                  </CardContent>
                </Card>

              </div>

              {/* Geolocation Section with Map Preview */}
              <Card className="bg-muted/30 border-border">
                <CardHeader className="p-3.5 pb-2">
                  <CardTitle className="text-xs font-bold text-emerald-600 uppercase flex items-center justify-between">
                    <span className="flex items-center gap-1.5">
                      <i className="fas fa-location-crosshairs"></i> Form Submission GPS Location
                    </span>
                    {selectedLead.location?.latitude && (
                      <a
                        href={`https://www.google.com/maps?q=${selectedLead.location.latitude},${selectedLead.location.longitude}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[11px] text-blue-600 hover:underline flex items-center gap-1"
                      >
                        Open Google Maps <i className="fas fa-external-link-alt text-[10px]"></i>
                      </a>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3.5 pt-0 space-y-3">
                  {selectedLead.location?.latitude ? (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-xs bg-background border rounded-lg p-2 font-mono">
                        <span>Latitude: <strong>{selectedLead.location.latitude}</strong></span>
                        <span>Longitude: <strong>{selectedLead.location.longitude}</strong></span>
                        <span>Accuracy: <strong>±{selectedLead.location.accuracy || 10} meters</strong></span>
                      </div>
                      
                      {/* Leaflet OSM Map Preview */}
                      <div className="h-44 rounded-xl overflow-hidden border border-border">
                        <OSMMapPreview
                          latitude={selectedLead.location.latitude}
                          longitude={selectedLead.location.longitude}
                          popupText={`Submission Location: ${selectedLead.formData?.fullName}`}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="text-xs text-muted-foreground italic py-2">
                      GPS coordinates were not allowed or captured during form submission.
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* PDF Section 4: Required Documents Gallery */}
              <Card className="bg-muted/30 border-border">
                <CardHeader className="p-3.5 pb-2">
                  <CardTitle className="text-xs font-bold text-amber-600 uppercase flex items-center gap-1.5">
                    <i className="fas fa-folder-open"></i> 4. REQUIRED DOCUMENTS ATTACHED ({getDocCount(selectedLead.documents)})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3.5 pt-0">
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    {Object.entries(selectedLead.documents || {}).flatMap(([cat, docList]) => 
                      docList.map(doc => (
                        <div
                          key={doc.id}
                          onClick={() => {
                            setSelectedDoc(doc);
                            setShowDocModal(true);
                          }}
                          className="bg-background border border-border p-3 rounded-xl hover:border-amber-500 cursor-pointer transition-all space-y-2 group shadow-sm"
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] font-bold uppercase text-amber-600 truncate">
                              {cat === 'drivers_license_front' ? "Photo ID #1 — License (Front)" :
                               cat === 'drivers_license_back' ? "Photo ID #1 — License (Back)" :
                               cat === 'drivers_license' ? "Photo ID #1 (License)" :
                               cat === 'government_id_front' ? "Photo ID #2 — Govt ID (Front)" :
                               cat === 'government_id_back' ? "Photo ID #2 — Govt ID (Back)" :
                               cat === 'government_id' ? "Photo ID #2 (Govt ID)" : "Driving Abstract"}
                            </span>
                            <i className="fas fa-expand text-muted-foreground group-hover:text-amber-500 text-xs"></i>
                          </div>

                          {doc.type === 'image' && doc.dataUrl && !doc.dataUrl.startsWith('[') ? (
                            <img src={doc.dataUrl} alt={doc.name} className="h-24 w-full object-cover rounded-lg border" />
                          ) : doc.type === 'image' ? (
                            <div className="h-24 w-full bg-amber-500/10 border border-amber-500/20 rounded-lg flex flex-col items-center justify-center text-amber-600">
                              <i className="fas fa-image text-3xl mb-1"></i>
                              <span className="text-[10px] font-bold">Mobile Photo Attached</span>
                            </div>
                          ) : (
                            <div className="h-24 w-full bg-rose-50 border border-rose-200 rounded-lg flex flex-col items-center justify-center text-rose-600">
                              <i className="fas fa-file-pdf text-3xl mb-1"></i>
                              <span className="text-[10px] font-medium">PDF Document</span>
                            </div>
                          )}

                          <div className="truncate">
                            <p className="font-medium text-xs text-foreground truncate">{doc.name}</p>
                            <p className="text-[10px] text-muted-foreground">{doc.size}</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* PDF Section 5: Consent & Signature Info */}
              <Card className="bg-muted/30 border-border">
                <CardHeader className="p-3.5 pb-2">
                  <CardTitle className="text-xs font-bold text-amber-600 uppercase">
                    5. CONSENT & SIGNATURE
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3.5 pt-0 space-y-2 text-xs">
                  <div className="flex items-center gap-2 bg-background p-2.5 rounded border">
                    <i className="fas fa-circle-check text-emerald-500 text-sm"></i>
                    <span className="text-foreground font-medium">
                      Background & Driving Record Consent Confirmed by Applicant
                    </span>
                  </div>

                  <div className="flex justify-between items-center bg-background p-2.5 rounded border">
                    <div>
                      <span className="text-muted-foreground text-[10px] block">Digital Signature Name</span>
                      <span className="font-serif font-bold text-base text-foreground italic">{selectedLead.formData?.signatureName || selectedLead.formData?.fullName}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-muted-foreground text-[10px] block">Signed Date</span>
                      <span className="font-mono text-foreground">{selectedLead.formData?.signatureDate || new Date().toISOString().split('T')[0]}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Recruiter Live Comments */}
              <Card className="bg-muted/30 border-border">
                <CardHeader className="p-3.5 pb-2">
                  <CardTitle className="text-xs font-bold text-muted-foreground uppercase flex items-center gap-1.5">
                    <i className="fas fa-comments text-amber-600"></i> Recruiter Live Comments & Notes
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3.5 pt-0 space-y-3">
                  <div className="max-h-40 overflow-y-auto space-y-2 pr-1">
                    {(selectedLead.comments || []).map(comment => (
                      <div key={comment.id} className="bg-background border rounded-lg p-2.5 text-xs space-y-1">
                        <div className="flex justify-between items-center text-muted-foreground text-[10px]">
                          <span className="font-bold text-foreground">{comment.author}</span>
                          <span>{formatDateTime(comment.timestamp)}</span>
                        </div>
                        <p className="text-foreground">{comment.text}</p>
                      </div>
                    ))}
                  </div>

                  <div className="flex gap-2 pt-1">
                    <Input
                      placeholder="Add internal recruiter comment or interview notes..."
                      value={newCommentText}
                      onChange={(e) => setNewCommentText(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddComment(selectedLead.id)}
                      className="text-xs h-9 bg-background"
                    />
                    <Button
                      size="sm"
                      onClick={() => handleAddComment(selectedLead.id)}
                      className="h-9 px-4 text-xs bg-amber-600 hover:bg-amber-500 text-white"
                    >
                      Post Note
                    </Button>
                  </div>
                </CardContent>
              </Card>

            </div>
          )}

          <DialogFooter className="border-t pt-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {selectedLead && (
                <Button
                  variant="outline"
                  onClick={() => handleDeleteLead(selectedLead.id, selectedLead.referenceNumber)}
                  className="border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700 text-xs"
                >
                  <i className="fas fa-trash-can mr-1.5"></i>
                  Delete Lead
                </Button>
              )}

              {selectedLead && selectedLead.status === 'Archived' ? (
                <Button
                  variant="outline"
                  onClick={() => handleUnarchiveLead(selectedLead.id)}
                  className="border-slate-300 text-slate-700 hover:bg-slate-100 text-xs"
                >
                  <i className="fas fa-rotate-left mr-1.5"></i>
                  Unarchive Lead
                </Button>
              ) : selectedLead ? (
                <Button
                  variant="outline"
                  onClick={() => handleArchiveLead(selectedLead.id)}
                  className="border-amber-200 text-amber-600 hover:bg-amber-50 text-xs"
                >
                  <i className="fas fa-box-archive mr-1.5"></i>
                  Archive Lead
                </Button>
              ) : null}
            </div>

            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setShowDetailModal(false)}>
                Close
              </Button>

              {selectedLead && selectedLead.status !== 'Hired' && (
                <Button
                  onClick={() => {
                    handleHireDriver(selectedLead);
                    setShowDetailModal(false);
                  }}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <i className="fas fa-user-check mr-1.5"></i>
                  Hire Candidate
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Inspector Modal */}
      <Dialog open={showDocModal} onOpenChange={setShowDocModal}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-sm font-semibold flex items-center justify-between">
              <span>{selectedDoc?.name}</span>
              <span className="text-xs text-muted-foreground font-normal">{selectedDoc?.size}</span>
            </DialogTitle>
          </DialogHeader>

          {selectedDoc && (
            <div className="p-4 flex items-center justify-center bg-slate-900 rounded-xl min-h-[300px]">
              {selectedDoc.type === 'image' && selectedDoc.dataUrl && !selectedDoc.dataUrl.startsWith('[') ? (
                <img src={selectedDoc.dataUrl} alt={selectedDoc.name} className="max-h-[60vh] object-contain rounded shadow-lg" />
              ) : selectedDoc.type === 'image' ? (
                <div className="text-center space-y-3 text-slate-300 p-8">
                  <i className="fas fa-image text-6xl text-amber-500 animate-pulse"></i>
                  <p className="text-sm font-bold text-white">{selectedDoc.name}</p>
                  <p className="text-xs text-amber-300">Photo attached via mobile recruitment submission ({selectedDoc.size}).</p>
                </div>
              ) : (
                <div className="text-center space-y-3 text-slate-300">
                  <i className="fas fa-file-pdf text-6xl text-rose-500"></i>
                  <p className="text-sm font-medium">{selectedDoc.name}</p>
                  <a
                    href={selectedDoc.dataUrl}
                    download={selectedDoc.name}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white text-xs font-medium rounded-xl"
                  >
                    <i className="fas fa-download"></i> Download PDF Document
                  </a>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Mandatory Offer Letter & Hiring Modal */}
      <Dialog open={showHireModal} onOpenChange={setShowHireModal}>
        <DialogContent className="max-w-xl bg-card border-border text-foreground">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-emerald-500/15 text-emerald-600 flex items-center justify-center text-sm">
                <i className="fas fa-file-signature"></i>
              </span>
              <span>Hire Candidate & Attach Offer Letter</span>
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              An official signed Offer Letter must be attached to convert candidate into an active TMS Fleet Driver.
            </DialogDescription>
          </DialogHeader>

          {hiringLead && (
            <div className="space-y-4 py-2">
              {/* Candidate Quick Info Card */}
              <div className="bg-muted/60 border border-border p-3.5 rounded-xl flex items-center justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-sm text-foreground">{hiringLead.formData?.fullName}</span>
                    <Badge className="bg-blue-500/15 text-blue-600 text-[10px] font-mono">
                      {hiringLead.referenceNumber}
                    </Badge>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-3">
                    <span><i className="fas fa-id-card text-amber-500 mr-1"></i>{hiringLead.formData?.licenseClass || 'Class G'} ({hiringLead.formData?.licenseNumber || 'N/A'})</span>
                    <span><i className="fas fa-phone text-blue-500 mr-1"></i>{hiringLead.formData?.phone}</span>
                  </div>
                </div>
                <Badge className="bg-amber-500/15 text-amber-600 border border-amber-300 text-[10px]">
                  Pending Offer Letter
                </Badge>
              </div>

              {/* Offer Letter File Upload Dropzone */}
              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-foreground flex items-center justify-between">
                  <span>Attach Official Signed Offer Letter *</span>
                  <span className="text-[10px] text-amber-600 font-semibold">(Required before hiring)</span>
                </Label>

                {offerLetterFile ? (
                  <div className="bg-emerald-500/10 border border-emerald-500/30 p-3 rounded-xl flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-emerald-500/20 text-emerald-600 flex items-center justify-center">
                        <i className={`fas ${offerLetterFile.type === 'pdf' ? 'fa-file-pdf text-rose-500' : 'fa-file-image text-emerald-600'}`}></i>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-foreground">{offerLetterFile.name}</p>
                        <p className="text-[10px] text-muted-foreground">{offerLetterFile.size} · Uploaded just now</p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setOfferLetterFile(null)}
                      className="text-rose-500 hover:text-rose-600 hover:bg-rose-500/10 h-8 w-8 p-0 cursor-pointer"
                    >
                      <i className="fas fa-trash-can"></i>
                    </Button>
                  </div>
                ) : (
                  <label className="border-2 border-dashed border-border hover:border-amber-500/50 rounded-xl p-6 flex flex-col items-center justify-center gap-2 cursor-pointer bg-muted/30 hover:bg-muted/60 transition-all text-center">
                    <div className="w-10 h-10 rounded-full bg-amber-500/10 text-amber-600 flex items-center justify-center">
                      <i className="fas fa-cloud-arrow-up text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs font-bold text-foreground">Click to upload or drag & drop Offer Letter</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">Supports PDF, DOCX, PNG, JPG (Max 15MB)</p>
                    </div>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                      onChange={handleOfferLetterFileSelect}
                      className="hidden"
                    />
                  </label>
                )}
              </div>

              {/* Hiring Pay Rate & Start Date */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">Offered Wage / Pay Rate</Label>
                  <Input
                    placeholder="e.g. $23.50 / hr or $55,000 / yr"
                    value={hiringDetails.payRate}
                    onChange={(e) => setHiringDetails(prev => ({ ...prev, payRate: e.target.value }))}
                    className="h-9 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">Official Hire / Start Date</Label>
                  <Input
                    type="date"
                    value={hiringDetails.startDate}
                    onChange={(e) => setHiringDetails(prev => ({ ...prev, startDate: e.target.value }))}
                    className="h-9 text-xs"
                  />
                </div>
              </div>

              {/* Internal Recruiter Notes */}
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Contract / Hiring Notes (Optional)</Label>
                <Input
                  placeholder="e.g. Approved by Dispatch Lead. Completed 3-day sprinter van orientation."
                  value={hiringDetails.notes}
                  onChange={(e) => setHiringDetails(prev => ({ ...prev, notes: e.target.value }))}
                  className="h-9 text-xs"
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowHireModal(false)} disabled={isSubmittingHire}>
              Cancel
            </Button>
            <Button
              onClick={executeConfirmHire}
              disabled={!offerLetterFile || isSubmittingHire}
              className={`text-white font-semibold transition-all cursor-pointer ${
                offerLetterFile
                  ? 'bg-emerald-600 hover:bg-emerald-500 shadow-md shadow-emerald-600/20'
                  : 'bg-muted text-muted-foreground cursor-not-allowed opacity-50'
              }`}
            >
              {isSubmittingHire ? (
                <>
                  <i className="fas fa-spinner animate-spin mr-2"></i>
                  Hiring & Pushing to TMS...
                </>
              ) : (
                <>
                  <i className="fas fa-user-check mr-2"></i>
                  Confirm Hire & Push to TMS
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
